import * as Sentry from "@sentry/react";

export interface TelemetryEvent {
  type: "error" | "warning" | "metric";
  category: string;
  message: string;
  context?: Record<string, unknown>;
  timestamp: number;
}

function hasSentry(): boolean {
  return !!import.meta.env.VITE_SENTRY_DSN;
}

function formatEvent(event: TelemetryEvent): string {
  const ctx = event.context ? ` ${JSON.stringify(event.context)}` : "";
  return `[${event.category}] ${event.message}${ctx}`;
}

export const telemetry = {
  captureError(category: string, error: unknown, context?: Record<string, unknown>) {
    const event: TelemetryEvent = {
      type: "error",
      category,
      message: error instanceof Error ? error.message : String(error),
      context,
      timestamp: Date.now(),
    };

    if (hasSentry()) {
      Sentry.captureException(error, {
        tags: { source: category, ...(context?.status != null ? { status: String(context.status) } : {}) },
        extra: context,
      });
    }

    console.error(formatEvent(event));
  },

  captureWarning(category: string, message: string, context?: Record<string, unknown>) {
    const event: TelemetryEvent = {
      type: "warning",
      category,
      message,
      context,
      timestamp: Date.now(),
    };

    if (hasSentry()) {
      Sentry.captureMessage(message, {
        level: "warning",
        tags: { source: category },
        extra: context,
      });
    }

    console.warn(formatEvent(event));
  },

  captureMetric(name: string, value: number, context?: Record<string, unknown>, unit: string = "millisecond") {
    const event: TelemetryEvent = {
      type: "metric",
      category: "metric",
      message: `${name}=${value}`,
      context,
      timestamp: Date.now(),
    };

    if (hasSentry()) {
      const attributes: Record<string, string> = { value: String(value), unit };
      if (context) {
        for (const [k, v] of Object.entries(context)) {
          attributes[k] = String(v);
        }
      }
      Sentry.startSpan({ name, op: "metric", attributes }, () => {});
    }

    if (import.meta.env.DEV) {
      console.info(formatEvent(event));
    }
  },
};
